import type { JSONSchema6TypeName } from "json-schema";
import { type ExtendedJSONSchema, typeMap } from "./SystemSettings.types";

export type FieldEntry = [string, ExtendedJSONSchema & { title?: string }];

export interface GroupedFields {
  [sectionName: string]: FieldEntry[];
}

/**
 * Groups form fields by their section/group name and sorts them by order
 * @param properties - Schema properties from the form
 * @returns Object with section names as keys and array of [key, options] tuples as values
 */
export function groupFieldsBySection(
  properties: Record<string, any> | undefined
): GroupedFields {
  const grouped: GroupedFields = {};

  if (!properties) {
    return grouped;
  }

  Object.entries(properties).forEach(([key, options]) => {
    if (typeof options === "boolean") {
      return;
    }

    const sectionName = options.group || "General";

    if (!grouped[sectionName]) {
      grouped[sectionName] = [];
    }

    grouped[sectionName].push([
      key,
      options as ExtendedJSONSchema & { title?: string },
    ]);
  });

  // Sort fields within each section by order
  Object.keys(grouped).forEach((section) => {
    grouped[section].sort((a, b) => {
      const orderA = (a[1] as ExtendedJSONSchema).order ?? 999;
      const orderB = (b[1] as ExtendedJSONSchema).order ?? 999;
      return orderA - orderB;
    });
  });

  return grouped;
}

/**
 * Get sorted list of section names by group_order
 * @param grouped - Grouped fields object
 * @returns Array of section names, sorted by group_order (lower numbers first)
 */
export function getSectionNames(grouped: GroupedFields): string[] {
  const sections = Object.keys(grouped);

  // Sort sections by group_order from the first field in each group
  return sections.sort((a, b) => {
    const fieldsA = grouped[a];
    const fieldsB = grouped[b];

    // Get group_order from first field in each section
    const orderA = fieldsA[0]?.[1]?.group_order ?? 999;
    const orderB = fieldsB[0]?.[1]?.group_order ?? 999;

    return orderA - orderB;
  });
}

/**
 * Check if a section has at least one visible field
 * @param fields - Fields in the section
 * @param isDebugMode - Whether debug mode is enabled
 * @returns true if section has visible fields
 */
export function hasVisibleFields(
  fields: FieldEntry[],
  isDebugMode?: boolean
): boolean {
  if (isDebugMode) return true;

  return fields.some(([_key, options]) => {
    const isHidden = (options as ExtendedJSONSchema).is_hidden ?? false;
    return !isHidden;
  });
}

/**
 * Get list of sections with visible fields
 * @param grouped - Grouped fields object
 * @param isDebugMode - Whether debug mode is enabled
 * @returns Array of section names that have at least one visible field
 */
export function getVisibleSections(
  grouped: GroupedFields,
  isDebugMode: boolean
): string[] {
  const allSections = getSectionNames(grouped);

  return allSections.filter((section) =>
    hasVisibleFields(grouped[section] || [], isDebugMode)
  );
}

export interface SubgroupedFields {
  /** Undefined for fields with no `subgroup` — rendered without a header. */
  name?: string;
  fields: FieldEntry[];
}

/**
 * Split a section's fields into subgroups, ordered by first appearance
 * (i.e. by the lowest `order` of their fields, as `fields` arrives sorted).
 */
export function groupFieldsBySubgroup(fields: FieldEntry[]): SubgroupedFields[] {
  const bySubgroup = new Map<string | undefined, FieldEntry[]>();

  fields.forEach((entry) => {
    const name = entry[1].subgroup;
    const existing = bySubgroup.get(name);
    if (existing) existing.push(entry);
    else bySubgroup.set(name, [entry]);
  });

  return [...bySubgroup].map(([name, subgroupFields]) => ({
    name,
    fields: subgroupFields,
  }));
}

/** Scalar settings the form actually renders. */
export function visibleSettingFields(
  fields: FieldEntry[],
  isDebugMode: boolean
): FieldEntry[] {
  return fields.filter(
    ([, options]) =>
      typeMap[options.type as JSONSchema6TypeName] != null &&
      (isDebugMode || !options.is_hidden)
  );
}

/** Defaults that differ from current values. Secrets and missing defaults are skipped. */
export function schemaDefaultsToApply(
  fields: FieldEntry[],
  values: { [key: string]: unknown } | undefined
): [string, unknown][] {
  const updates: [string, unknown][] = [];
  for (const [key, options] of fields) {
    if (options.is_secret || options.default === undefined) continue;
    if (values?.[key] === options.default) continue;
    updates.push([key, options.default]);
  }
  return updates;
}
